import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SECTION_HEADING } from './fileCardStyles';
import SectionHelp from './SectionHelp';

export const PANEL_SHELL =
  'overflow-hidden rounded-xl border bg-card shadow-md transition-[border-color,box-shadow] duration-fast focus-within:border-brand focus-within:shadow-lg';

/** Exported because the collapsible sections make their header a <button>,
 * which cannot be Panel's own header element. */
export const PANEL_HEADER =
  'flex items-center gap-2 rounded-t-xl border-b border-b-border-brand bg-accent px-5 py-3';

export const PANEL_ICON = 'h-4 w-4 shrink-0 text-primary';

/** A step's section: a tinted strip carrying a mark, the title and its tip,
 * then the body. */
export default function Panel({
  icon: Icon,
  title,
  help,
  actions,
  className,
  bodyClassName,
  children,
}: {
  icon: LucideIcon;
  title: string;
  help?: string;
  /** Trailing controls in the strip, pushed to its right edge. */
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn(PANEL_SHELL, className)}>
      <div className={PANEL_HEADER}>
        <Icon className={PANEL_ICON} />
        <h3 className={SECTION_HEADING}>{title}</h3>
        {help && <SectionHelp title={title} body={help} />}
        {actions && (
          <>
            <span className="grow" />
            {actions}
          </>
        )}
      </div>
      <div className={cn('space-y-5 p-5', bodyClassName)}>{children}</div>
    </section>
  );
}
